// --- Our Modules --------------------------------------------------------------------------------
import type { Translation } from "./types";
// --- Logic --------------------------------------------------------------------------------------
const ar: Translation = {
    common: {
        loading: "جارٍ التحميل...",
        retry: "إعادة المحاولة",
        somethingWrong: "حدث خطأ، حاول مرة أخرى",
        logout: "الخروج",
        save: "حفظ",
        cancel: "إلغاء",
        more: "المزيد",
        close: "إغلاق",
        confirm: "تأكيد",
        done: "تم",
        copy: "نسخ",
        copied: "تم النسخ",
        share: "مشاركة",
        typeToConfirm: "اكتب «{word}» للتأكيد",
        filters: "الفلاتر",
        search: "بحث",
        applyFilters: "تطبيق",
        clearFilters: "مسح",
        back: "رجوع",
        edit: "تعديل",
        remove: "حذف",
        add: "إضافة",
        pagination: "التنقل بين الصفحات",
        previousPage: "الصفحة السابقة",
        nextPage: "الصفحة التالية",
        pageCount: "{count} عناصر من {total}",
        perPage: "لكل صفحة",
    },
    bulk: {
        select: "تحديد",
        selected: "{count} محدد",
        keep: "بدون تغيير",
        hint: "يُكتب على الجميع ما تملأه فقط",
        skipped: "{count} لم يكن من الممكن تعديلها",
        reviewTitle: "مراجعة التعديل",
        reviewHint: "سيُكتب كما في الأسفل على كل سجل من السجلات المحددة ({count})، ولن يُمس أي شيء آخر فيها.",
        willChange: "ما سيتغير",
        fieldHint: "سيُكتب هذا على كل سجل محدد. تراجع لترك الحقل كما هو.",
        resultTitle: "انتهى",
        changed: "{count} تم تعديلها",
        removed: "{count} إلى سلة المحذوفات",
        restored: "{count} تمت استعادتها",
        purged: "{count} حُذفت نهائيًا",
        failed: "{count} دون تعديل",
        nothingChanged: "لم يتغير شيء",
        revertAll: "تراجع عن الكل",
        andFailed: "المبيعات تملك حالة بعض هذه السجلات الآن، أو أنها لم تعد موجودة.",
        groupSet: "{count} محددة",
        groupCleared: "مُفرَّغة",
        written: "سيُكتب",
        pointPicked: "تم اختيار موقع",
        confirmRemove: "{count} محددة. نقلها إلى سلة المحذوفات؟",
        confirmRestore: "{count} محددة. استعادتها؟",
        confirmPurge: "{count} محددة. حذفها نهائيًا؟ لا يمكن التراجع عن هذا.",
        restoreMany: "استعادة",
        purgeMany: "حذف نهائي",
        exit: "إلغاء التحديد",
    },
    auth: {
        title: "تسجيل الدخول",
        subtitle: "أدخل رقم جوالك وكلمة المرور للمتابعة",
        phone: "رقم الجوال",
        phonePlaceholder: "5XXXXXXXX",
        password: "كلمة المرور",
        passwordPlaceholder: "••••••••",
        login: "دخول",
        selectOrganization: "اختر المنشأة",
        selectOrganizationHint: "لديك صلاحية الدخول لأكثر من منشأة",
        noOrganizations: "لا توجد منشأة مرتبطة بحسابك",
        phoneRequired: "رقم الجوال مطلوب",
        passwordRequired: "كلمة المرور مطلوبة",
    },
    sideBar: {
        home: "الرئيسية",
        properties: "العقارات",
        crm: "العملاء",
        drive: "الملفات",
        team: "الفريق",
        settings: "الإعدادات",
        subscription: "الاشتراك",
        support: "المساعدة",
        trash: "المحذوفات",
    },
    profile: {
        myProfile: "حسابي",
        subtitle: "بياناتك الشخصية وما تصل إليه في كل منشأة.",
        editProfile: "تعديل البيانات",
        memberSince: "عضو منذ",
        fields: { name: "الاسم", phone: "رقم الجوال", email: "البريد الالكتروني", gender: "الجنس", birthDate: "تاريخ الميلاد" },
        genders: { male: "ذكر", female: "أنثى", unset: "غير محدد" },
        phoneHint: "رقم جوالك هو هويتك في النظام، ولا يغيّره إلا الدعم.",
        picture: "الصورة الشخصية",
        pictureHint: "تظهر لزملائك في كل منشأة تعمل بها.",
        changePicture: "تغيير الصورة",
        removePicture: "حذف الصورة",
        uploadFailed: "تعذّر رفع الصورة، حاول مرة أخرى",
        notSet: "غير مضاف",
        security: "كلمة المرور",
        securityHint: "غيّرها متى شئت. نسيانها يحتاج الدعم لإصدار كلمة جديدة.",
        changePassword: "تغيير كلمة المرور",
        currentPassword: "كلمة المرور الحالية",
        newPassword: "كلمة المرور الجديدة",
        confirmPassword: "تأكيد كلمة المرور",
        passwordHint: "تغيير كلمة المرور ينهي جلساتك على الأجهزة الأخرى.",
        passwordTooShort: "كلمة المرور يجب ألا تقل عن {count} خانات",
        passwordMismatch: "كلمتا المرور غير متطابقتين",
        temporaryPassword: "كلمة مرورك مؤقتة، غيّرها الآن",
        organizations: "المنشآت",
        organizationsHint: "المنشآت التي تعمل بها، والدور الذي تحمله في كل واحدة.",
        activeOrganization: "المنشأة الحالية",
        switchTo: "الانتقال",
        preferences: "التفضيلات",
        preferencesHint: "تخص هذا المتصفح وحده، ولا تتغير على أجهزتك الأخرى.",
        language: "لغة اللوحة",
    },
    subscription: {
        title: "الاشتراك",
        statuses: {
            pending: "بانتظار التحويل",
            active: "فعّال",
            coming: "مجدول",
            grace: "مهلة السماح",
            ended: "منتهٍ",
            cancelled: "ملغي",
        },
        periods: { quarterly: "ربع سنوي", annually: "سنوي" },
        periodEvery: { quarterly: "كل 3 أشهر", annually: "كل سنة" },
        choosePlan: "اختر باقتك",
        choosePlanHint: "باقة واحدة بفترتين، وكل شيء مفتوح عدا مساحة التخزين.",
        renewTitle: "تجديد الاشتراك",
        renewHint: "الفترة الجديدة تبدأ بعد انتهاء فترتك الحالية، فلا يضيع منك يوم.",
        saveLabel: "وفّر {percent}%",
        storageIncluded: "{gb} جيجابايت تخزين",
        gb: "جيجابايت",
        extraStorage: "مساحة إضافية",
        extraStorageHint: "{gb} جيجابايت مقابل {price} {currency}، تُضاف للباقة وتنتقل معك عند التجديد.",
        packLabel: "حزمة",
        totalStorage: "إجمالي المساحة",
        total: "الإجمالي",
        request: "اطلب الاشتراك",
        reviewTitle: "مراجعة الطلب",
        reviewHint: "بعد التأكيد حوّل المبلغ وأرسل لنا الإيصال، ويُفعّل الاشتراك فور تأكيد التحويل.",
        planLine: "الباقة {period}",
        addonsLine: "{count} × مساحة إضافية",
        daysLeft: "يوم متبقٍ",
        startDate: "تاريخ البداية",
        endDate: "تاريخ الانتهاء",
        renew: "تجديد",
        renewNow: "جدّد الآن",
        reactivate: "إعادة التفعيل",
        graceTitle: "انتهت فترة اشتراكك",
        graceNote: "حسابك يعمل بالكامل خلال مهلة السماح، ثم يصبح للقراءة فقط.",
        graceEnds: "تنتهي المهلة في",
        endedTitle: "حسابك للقراءة فقط",
        endedNote: "بياناتك كاملة ولم يُحذف منها شيء. جدّد الاشتراك ليعود كل شيء كما كان.",
        freeTitle: "أنت على الباقة المجانية",
        freeNote: "حدود بسيطة تكفي للتجربة. الاشتراك يفتح كل شيء عدا مساحة التخزين.",
        pendingTitle: "طلبك مسجّل بانتظار التحويل",
        pendingNote: "حوّل المبلغ ثم أرسل لنا الإيصال، ويُفعّل اشتراكك فور تأكيده.",
        pendingRenewalTitle: "طلب تجديد بانتظار التحويل",
        pendingRenewalNote: "اشتراكك الحالي يعمل كالمعتاد، والفترة الجديدة تُجدول بعده فور تأكيد التحويل.",
        amountLabel: "المبلغ المطلوب",
        payBy: "آخر موعد للتحويل",
        requestedBy: "طلبه",
        howToPay: "طريقة السداد",
        bankLabel: "البنك",
        accountName: "اسم الحساب",
        iban: "الآيبان",
        sendReceipt: "إرسال الإيصال واتساب",
        usageTab: "الاستهلاك",
        currentPeriod: "الفترة الحالية",
        nextPeriod: "الفترة القادمة",
        used: "المستهلك",
        available: "المتاح",
        buyStorage: "شراء مساحة",
        buyStorageTitle: "مساحة تخزين إضافية",
        buyStorageHint: "تُضاف إلى فترتك الجارية فور تأكيد التحويل، وتنتقل معك عند التجديد.",
        packs: "عدد الحزم",
        storagePendingTitle: "طلب مساحة بانتظار التحويل",
        storagePendingNote: "لن تُضاف المساحة إلى باقتك قبل تأكيد التحويل.",
        withdrawStorage: "سحب طلب المساحة",
        withdrawStorageConfirm: "سيُلغى طلب المساحة ويمكنك طلب عدد آخر بعدها.",
        addedStorage: "مساحة مضافة",
        withdraw: "سحب الطلب",
        withdrawConfirm: "سيُلغى الطلب ويمكنك طلب باقة أخرى بعدها.",
        comingTitle: "فترة مجدولة",
        comingNote: "مدفوعة بالفعل، وتبدأ تلقائيًا بعد انتهاء فترتك الحالية.",
        comingStarts: "تبدأ في",
        usageTitle: "الاستهلاك",
        usageHint: "ما استهلكته من حدود باقتك الحالية.",
        projects: "المشاريع",
        units: "الوحدات",
        customers: "العملاء",
        team: "الفريق",
        storage: "التخزين",
        unlimited: "غير محدود",
        ofLimit: "من {limit}",
        needMoreStorage: "تحتاج مساحة أكبر؟",
        needMoreStorageNote: "تُضاف المساحة لفترتك الجارية فورًا وتنتقل معك عند التجديد.",
        contactUs: "تواصل معنا",
        historyTitle: "السجل",
        historyEmpty: "لا توجد فترات سابقة.",
        paidOn: "سُدّد في",
        cancelledOn: "أُلغي في",
        requestedOn: "طُلب في",
    },
    settings: {
        catalogTitle: "كتالوجك العام",
        catalogHint: "كل ما في هذه الشاشة هو ما يراه الزوار في كتالوجك.",
        identity: "الهوية",
        brand: "العلامة",
        contacts: "بيانات التواصل",
        contactsHint: "تظهر في الكتالوج ليتمكن المشتري من التواصل معك دون تعبئة نموذج.",
        languagesHint: "اللغات التي يُنشر بها كتالوجك، ولا تُعرض على الزوار سواها.",
        aboutHint: "فقرة قصيرة عن المنشأة، تظهر في بطاقة الشركة داخل الكتالوج.",
        coverHint: "تظهر خلف الشعار في بطاقة الشركة. بدونها يُستخدم لون العلامة.",
        colorHint: "يُستخدم في الأزرار والعناصر البارزة في الكتالوج.",
        logoHint: "اختر إحدى صورك، أو ارفع صورة جديدة.",
        noLogo: "بدون شعار",
        saved: "تم الحفظ",
        nameRequired: "اسم المنشأة مطلوب",
        languagesRequired: "اختر لغة واحدة على الأقل",
        colorInvalid: "اللون يجب أن يكون قيمة ست عشرية مثل ‎#1F6F5C",
        fields: {
            name: "اسم المنشأة",
            languages: "لغات النشر",
            color: "لون العلامة",
            logo: "الشعار",
            about: "نبذة عن المنشأة",
            cover: "صورة الغلاف",
            phone: "الجوال",
            whatsapp: "واتساب",
            email: "البريد الإلكتروني",
            city: "المدينة",
            address: "العنوان",
            crNumber: "السجل التجاري",
        },
    },
    team: {
        add: "إضافة",
        addTitle: "إضافة عضو",
        addHint: "إذا كان الرقم مسجلًا من قبل، سينضم بحسابه الحالي بكلمة مروره الحالية.",
        name: "الاسم",
        namePlaceholder: "الاسم الكامل",
        nameRequired: "الاسم مطلوب",
        phoneRequired: "رقم الجوال مطلوب",
        role: "الصلاحية",
        empty: "لا يوجد أعضاء بعد",
        emptyHint: "أضف من يعمل في هذه المنشأة. الرقم المسجَّل مسبقًا ينضم بحسابه الحالي.",
        you: "(أنت)",
        temporaryPassword: "كلمة مرور مؤقتة",
        passwordTitle: "كلمة المرور",
        passwordHint: "شارك كلمة المرور هذه مع {name}. تظهر مرة واحدة فقط — بعدها يمكن إصدار كلمة جديدة ما دام لم يغيّرها.",
        confirmRole: "تحويل {name} إلى {role}؟",
        confirmRemove: "إزالة {name} من منشأتك؟ لن يُحذف حسابه، فقط صلاحيته هنا.",
        confirmTransfer: "نقل ملكية المنشأة إلى {name}؟ ستصبح أنت مديرًا، ولا يمكنك التراجع بنفسك.",
        confirmPassword: "إصدار كلمة مرور جديدة لـ {name}؟ ستنتهي كل جلساته الحالية.",
        transferWord: "نقل",
        actions: { turnTo: "تحويل إلى {role}", reissuePassword: "إصدار كلمة مرور", transferOwnership: "نقل الملكية", remove: "إزالة" },
    },
    drive: {
        upload: "رفع",
        uploadTitle: "رفع ملفات",
        uploadHint: "صور، فيديوهات، مستندات وملفات ثلاثية الأبعاد. كل ما ترفعه يُحسب من مساحتك.",
        pick: "اختر الملفات",
        pickHint: "أو أسقطها هنا",
        selected: "{count} ملف مختار",
        clear: "إلغاء",
        root: "ملفاتي",
        allFiles: "كل الملفات",
        search: "ابحث في الملفات",
        empty: "لا توجد ملفات بعد",
        gallery: "المعرض",
        slots: {
            picture: "الصورة الشخصية",
            gallery: "معرض الصور",
            logo: "الشعار",
            paper: "العقد الموقّع",
            receipt: "الإيصالات",
            identity: "الهوية",
        },
        recordFiles: "ملفات السجل",
        emptyHint: "ارفع من مشروع أو وحدة أو عقد، ويُحفظ الملف تحته تلقائيًا.",
        emptyRecord: "لا توجد ملفات على هذا السجل بعد.",
        emptyRecordHint: "الصور والمخططات والأوراق الموقّعة التي تُرفع هنا تبقى مع السجل.",
        generalFiles: "ملفات عامة",
        emptySlot: "لا شيء هنا بعد.",
        pickFromDrive: "اختر من الملفات",
        emptyGallery: "لا توجد صور بعد",
        otherFiles: "الملفات",
        emptyFolder: "هذا المجلد فارغ",
        used: "المستخدم",
        ofAllowed: "من {allowed}",
        freeQuota: "المساحة المجانية",
        full: "المساحة ممتلئة",
        files: "ملف",
        name: "الاسم",
        namePlaceholder: "اسم تعرفه لاحقًا",
        tags: "الوسوم",
        tagsPlaceholder: "افصل بينها بفاصلة",
        renameTitle: "تغيير اسم الملف",
        confirmRemove: "حذف {name}؟ لا يمكن التراجع، والملف يُحذف فورًا.",
        unnamedRecord: "سجل",
        noPreview: "لا تتوفر معاينة لهذا النوع",
        loose: "ملفات غير مرتبطة",
        categories: { image: "الصور", video: "الفيديوهات", document: "المستندات", model: "ملفات ثلاثية الأبعاد", archive: "ملفات مضغوطة" },
        owners: {
            Organization: "المنشأة",
            Project: "المشاريع",
            Unit: "الوحدات",
            Customer: "العملاء",
            Contract: "العقود",
            Reservation: "الحجوزات",
            Installment: "الأقساط",
            Warranty: "الضمانات",
            others: "أخرى",
        },
        actions: { download: "تنزيل", rename: "تغيير الاسم", replace: "استبدال", remove: "حذف" },
    },
    uploads: {
        uploading: "جارٍ رفع {done} من {total}",
        allDone: "اكتمل الرفع",
        someFailed: "فشل {count}",
        waiting: "بالانتظار",
        cancelled: "ملغي",
        retry: "إعادة المحاولة",
        clear: "إخفاء",
        collapse: "طي",
        expand: "توسيع",
    },
    crm: {
        title: "العملاء",
        stats: {
            new: "اهتمامات جديدة",
            offer: "عروض",
            won: "مكسوبة",
        },
        actions: {
            contact: "تواصل",
            profile: "الملف",
            open: "فتح الاهتمام",
        },
        picture: "الصورة",
        pictureHint: "اختياري",
        removePicture: "بدون صورة",
        confirmBlock: "حظر {name}؟ ستُقبل طلباته من الكتالوج ثم تُهمل، ولن يُخبر بذلك.",
        confirmUnblock: "إلغاء حظر {name}؟ ستُقبل طلباته من جديد.",
        noOpenLead: "لا يوجد اهتمام مفتوح",
        ownsUnit: "يملك",
        leads: "الاهتمامات",
        customers: "العملاء",
        addLead: "اهتمام جديد",
        addCustomer: "عميل جديد",
        editCustomer: "تعديل العميل",
        editLead: "تعديل الاهتمام",
        searchLeads: "ابحث بالاسم أو الجوال أو البريد",
        searchCustomers: "ابحث بالاسم أو الجوال أو البريد",
        emptyLeads: "لا توجد اهتمامات بعد، أضف أولها أو انتظر وصول اهتمام من الكتالوج.",
        leadNotFound: "هذا الاهتمام غير موجود.",
        leadNotFoundHint: "قد يكون محذوفًا، وفي هذه الحالة ما زال في سلة المحذوفات.",
        customerNotFound: "هذا الشخص غير موجود.",
        customerNotFoundHint: "قد يكون محذوفًا، وفي هذه الحالة ما زال في سلة المحذوفات.",
        emptyCustomers: "لا أحد هنا بعد.",
        emptyInteractions: "لم يُسجَّل شيء بعد.",
        emptyInteractionsHint: "كل مكالمة ورسالة وزيارة — وما هو محجوز منها لوقت لاحق.",
        emptyNotes: "لا توجد ملاحظات.",
        emptyReservations: "لم يصل هذا الاهتمام إلى حجز.",
        reservations: "الحجوزات",
        general: "اهتمام عام",
        unnamed: "بدون اسم",
        notSet: "—",
        never: "لا يوجد",
        logInteraction: "تسجيل تواصل",
        bookFollowUp: "حجز متابعة",
        editInteraction: "تعديل السجل",
        closeLead: "إغلاق",
        reopenLead: "إعادة فتح",
        moveStage: "نقل المرحلة",
        addNote: "إضافة ملاحظة",
        writeNote: "اكتب ملاحظة",
        block: "حظر",
        unblock: "إلغاء الحظر",
        blocked: "محظور",
        notBlocked: "غير محظور",
        blockedHint: "تُقبل طلباته من الكتالوج ثم تُهمل، ولا يُخبَر بذلك.",
        closedBadge: "مغلق",
        showClosed: "إظهار المغلقة",
        unassigned: "بلا مسؤول",
        lastContact: "آخر تواصل",
        interestedIn: "مهتم بـ",
        budget: "الميزانية",
        wonHint: "يتحول الاهتمام إلى مكسوب عند الحجز أو التعاقد.",
        backwardsHint: "يمكنك تقديم الاهتمام للأمام فقط، وإن توقف فسجّله خسارة مع السبب.",
        closeHint: "لا يُغلق إلا اهتمام لم يُعمل عليه، والإغلاق ليس خسارة، بل تنظيف للتكرار من اللوحة.",
        lostHint: "وضّح السبب، وإلا لن يفرّق التقرير بين خسارة حقيقية وتنظيف.",
        followUpHint: "اختر تاريخًا في المستقبل ليتحول هذا إلى متابعة مستحقة عليك.",
        mergedHint: "لديه اهتمام مفتوح بالفعل، فأُضيف هذا إليه.",
        conflictTitle: "لديك عميل آخر بنفس رقم الجوال",
        conflictHint: "الشخص الواحد سجل واحد، افتح السجل الذي يحمل الرقم الآن أو أعد هذا برقم مختلف.",
        viewCustomer: "عرض ذلك العميل",
        changePhone: "تغيير رقم هذا",
        newPhone: "رقم الجوال الجديد",
        interests: "الاهتمامات",
        addInterest: "تسجيل ما يبحث عنه",
        editInterest: "تعديل ما يبحث عنه",
        emptyInterests: "لم يُسجَّل أي اهتمام بعد.",
        emptyInterestsHint: "سجّل ما يبحث عنه ليعرفه من يتابعه بعدك.",
        interestsTitle: "ما يبحث عنه",
        confirmRemoveInterest: "حذف هذا السطر؟",
        confirmRemoveNote: "حذف هذه الملاحظة؟",
        confirmStage: "نقل هذا الاهتمام إلى مرحلة «{stage}»؟",
        responsible: "المسؤول",
        activity: "النشاط",
        memberSince: "عميل منذ",
        openNote: "فتح الملاحظة",
        editNote: "تعديل الملاحظة",
        noteText: "نص الملاحظة",
        holderSold: "مباعة لـ",
        holderRented: "مؤجرة لـ",
        holderReserved: "محجوزة لـ",
        holderUntil: "حتى",
        holderDeposit: "العربون",
        emptyPersonLeads: "لا توجد اهتمامات لهذا الشخص بعد.",
        emptyPersonLeadsHint: "افتح اهتمامًا بما يسأل عنه.",
        emptyProjectLeads: "لا توجد اهتمامات على هذا المشروع بعد.",
        emptyProjectLeadsHint: "أضف اهتمامًا لكل من سأل عن هذا المشروع.",
        emptyUnitLeads: "لا توجد اهتمامات على هذه الوحدة بعد.",
        emptyUnitLeadsHint: "أي طلب يُسجَّل على هذه الوحدة يظهر هنا، من المعرض أو من فريقك.",
        recordedBy: "سجّله",
        addInteraction: "تسجيل تواصل",
        pastOrPlanned: "إضافة تواصل سابق",
        pastOrPlannedOff: "جدولة تواصل جديد",
        plannedHint: "سيظهر هذا في المتابعات المستحقة، واختر تاريخًا في المستقبل.",
        pastHint: "سجّل ما حدث فعلًا، ولا يمكن اختيار تاريخ في المستقبل.",
        schedule: "المتابعات المجدولة",
        voiceStart: "إملاء الملخص",
        voiceStop: "إيقاف الإملاء",
        voiceUnsupported: "الإملاء الصوتي غير مدعوم في هذا المتصفح",
        payMethod: "طريقة الدفع",
        rangeFrom: "من",
        rangeTo: "إلى",
        all: "الكل",
        today: "اليوم",
        logWhatHappened: "سجّل ما حدث",
        logWhatHappenedHint: "حان موعدها — اكتب نتيجتها.",
        interactionDetails: "التواصل",
        answered: "تم الرد",
        answeredHint: "لا نتيجة قبل أن يحدث",
        subjectLead: "اهتمام",
        noSummary: "لم يُكتب شيء.",
        allInterests: "كل الاهتمامات",
        mineInterests: "اهتماماتي",
        assignedToMe: "مسندة إليّ",
        allContacts: "كل التواصلات",
        mineContacts: "تواصلاتي",
        frozenPast: "حدث بالفعل، فلا يتغير منه إلا النتيجة والملخص والملاحظات والقناة.",
        tabs: {
            timeline: "التواصل",
            details: "التفاصيل",
            notes: "الملاحظات",
            leads: "الاهتمامات",
            files: "الملفات",
        },
        due: {
            title: "المتابعات",
            overdue: "متأخرة",
            upcoming: "قادمة",
            open: "مفتوحة",
            all: "الكل",
        },
        fields: {
            name: "الاسم",
            phone: "الجوال",
            email: "البريد الالكتروني",
            nationalId: "الهوية",
            status: "الحالة",
            kind: "نوع العقار",
            project: "المشروع",
            unit: "الوحدة",
            source: "وصل من",
            stage: "المرحلة",
            financing: "طريقة الدفع",
            budgetMin: "الميزانية من",
            budgetMax: "الميزانية إلى",
            assignees: "المسؤولون",
            lostReason: "سبب الخسارة",
            closeReason: "سبب الإغلاق",
            channel: "الوسيلة",
            category: "النوع",
            direction: "الاتجاه",
            outcome: "النتيجة",
            date: "التاريخ",
            summary: "الملخص",
            notes: "ملاحظات",
            firstContact: "أول تواصل",
        },
        stages: {
            new: "جديد",
            contacted: "تم التواصل",
            qualified: "مؤهل",
            offer: "عرض",
            won: "مكسوب",
            lost: "خسارة",
        },
        statuses: {
            lead: "مهتم",
            client: "عميل",
        },
        sources: {
            catalog: "الكتالوج",
            manual: "إدخال يدوي",
            call: "مكالمة",
            whatsapp: "واتساب",
            walkIn: "زيارة مباشرة",
            referral: "ترشيح",
            exhibition: "معرض",
            snapchat: "سناب شات",
            instagram: "إنستغرام",
            tiktok: "تيك توك",
            facebook: "فيسبوك",
            x: "إكس",
            google: "جوجل",
            portal: "منصة عقارية",
            other: "أخرى",
        },
        lostReasons: {
            price: "السعر",
            timing: "التوقيت",
            boughtElsewhere: "اشترى من مكان آخر",
            unreachable: "تعذّر الوصول إليه",
            notSerious: "غير جاد",
            other: "أخرى",
        },
        closeReasons: {
            duplicate: "مكرر",
            superseded: "استُبدل بآخر",
            mistake: "أُدخل بالخطأ",
            other: "أخرى",
        },
        interestStatuses: {
            active: "مهتم",
            hesitant: "متردد",
            stopped: "توقف",
        },
        financing: {
            cash: "كاش",
            mortgage: "تمويل عقاري",
        },
        channels: {
            call: "مكالمة",
            whatsapp: "واتساب",
            sms: "رسالة",
            email: "بريد",
            visit: "زيارة",
            catalog: "الكتالوج",
            other: "أخرى",
        },
        categories: {
            marketing: "تسويق",
            response: "رد",
            followUp: "متابعة",
            guarantee: "ضمان",
            other: "أخرى",
        },
        directions: {
            inbound: "مستلمة",
            outbound: "صادرة",
        },
        outcomes: {
            completed: "تم",
            failed: "لم يرد",
            pending: "لم يحدث بعد",
        },
        counting: {
            leads: "اهتمام",
            open: "مفتوح",
            interactions: "سجل",
        },
        confirmRemoveLead: "نقل اهتمام {name} إلى المحذوفات؟",
        confirmRemoveCustomer: "نقل {name} إلى المحذوفات؟",
        confirmRemoveInteraction: "نقل هذا التواصل إلى المحذوفات؟",
    },
    sales: {
        title: "المبيعات",
        reserve: "حجز",
        sell: "بيع",
        rent: "إيجار",
        closeDeal: "إغلاق الصفقة",
        byReservation: "حجز الوحدة",
        bySale: "عقد بيع أو إيجار",
        contracts: "العقود",
        reservations: "الحجوزات",
        installments: "الأقساط",
        plans: "خطط السداد",
        emptyContracts: "لا يوجد عقد أو حجز هنا بعد.",
        emptyContractsHint: "العقود والحجوزات وخطط السداد الخاصة بهذا السجل تظهر هنا معًا.",
        emptyReservations: "لا توجد حجوزات هنا بعد.",
        emptyReservationsHint: "الحجز يمسك الوحدة باسم العميل حتى يتحول لعقد أو يُلغى.",
        emptyInstallments: "لا يوجد مستحق الآن.",
        emptyInstallmentsHint: "تُنشأ الأقساط مع خطة سداد عند تسجيل عقد.",
        pickUnit: "اختر الوحدة",
        pickCustomer: "لمن؟",
        pickFromInterests: "من اهتماماته",
        pickAnyUnit: "أي وحدة",
        deposit: "العربون",
        expiresAt: "ينتهي الحجز في",
        signedAt: "تاريخ التوقيع",
        startsAt: "يبدأ في",
        endsAt: "ينتهي في",
        amount: "المبلغ",
        contractDetails: "تفاصيل العقد",
        reservationDetails: "تفاصيل الحجز",
        listPrice: "سعر الوحدة المعلن",
        discount: "الخصم",
        priorHold: "الحجز الذي سبقه",
        recordedBy: "سجّله",
        recordedAt: "تاريخ التسجيل",
        papers: "الأوراق والمرفقات",
        noPaper: "لم تُرفع نسخة العقد الموقّع بعد.",
        noFiles: "لا توجد مرفقات على هذا السجل.",
        otherFile: "ملف آخر",
        addFile: "إضافة ملف",
        paperHint: "ارفع نسخة العقد الموقّع الآن، أو لاحقًا من العقد نفسه.",
        addPlan: "خطة سداد",
        planCount: "عدد الأقساط",
        planPeriod: "كل كم شهر",
        planStart: "أول قسط في",
        dueAt: "تاريخ الاستحقاق",
        paid: "مدفوع",
        remaining: "المتبقي",
        overdue: "متأخرة",
        upcoming: "قادمة",
        addPayment: "تسجيل دفعة",
        viewSchedule: "جدول الأقساط",
        paymentAmount: "المبلغ المدفوع",
        paidAt: "تاريخ الدفع",
        cancelReservation: "إلغاء الحجز",
        cancelContract: "إلغاء العقد",
        confirmCancelReservation: "إلغاء هذا الحجز وإعادة الوحدة للمتاح؟",
        confirmCancelContract: "إلغاء هذا العقد وإعادة الوحدة للمتاح؟",
        installmentOf: "قسط {number} من {count}",
        statuses: { pending: "مستحق", paid: "مدفوع", cancelled: "ملغي" },
        planStatuses: { active: "جارية", completed: "مكتملة", cancelled: "ملغاة" },
        emptyPlans: "لا توجد خطط سداد بعد.",
        emptyPlansHint: "تُكتب الخطة عندما يُسدَّد العقد على دفعات.",
        contractStatuses: { live: "ساري", cancelled: "ملغي" },
        reservationStatuses: { active: "ساري", expired: "منتهي", done: "تحول لعقد", cancelled: "ملغي" },
        cancelReasons: { expired: "انتهت مدته", customerWithdrew: "انسحب العميل", mistake: "أُدخل بالخطأ", terminated: "فُسخ", other: "أخرى" },
    },
    warranties: {
        title: "الضمانات",
        tab: "الضمانات",
        one: "ضمان",
        state: { notStarted: "لم يبدأ", active: "ساري", expiring: "قارب على الانتهاء", expired: "منتهٍ", unknown: "بلا تاريخ انتهاء" },
        stateHint: {
            notStarted: "يبدأ احتسابه عند بيع الوحدة.",
            active: "ساري",
            expiring: "ينتهي خلال {days} يومًا",
            expired: "انتهت التغطية",
            unknown: "لم يُحدَّد تاريخ انتهائه",
        },
        provider: "الضامن",
        providerPhone: "جوال الضامن",
        months: "مدة التغطية",
        monthsRequired: "مدة التغطية بالأشهر مطلوبة",
        startsAt: "يبدأ",
        endsAt: "ينتهي",
        daysLeft: "بقي {days} يومًا",
        remaining: "المتبقي",
        covers: "يغطي",
        notStartedHint: "ما تأتي به هذه الوحدة. البيع يحوّل كل بند منها إلى ضمان متابَع.",
        contactAbout: "سجّل تواصلًا",
        edit: "تعديل الضمان",
        empty: "لا ضمانات على هذه الوحدة.",
        emptyHint: "سجّل ما هو مغطى، ولكم من الوقت، ومن يضمنه — والبيع يبدأ العدّ.",
        emptyTemplates: "لا تغطية بعد.",
        emptyTemplatesHint: "أضف ما تأتي به الوحدة قبل بيعها.",
        emptyCustomer: "لا ضمانات لهذا العميل.",
        emptyCustomerHint: "تظهر هنا حين تحمل وحدة اشتراها ضمانًا.",
        lockedBySale: "الوحدة مباعة، فضماناتها صارت سجلات حية ولا تُعدَّل من هنا.",
        allUnits: "كل الوحدات",
        allStates: "أي حالة",
        allSubjects: "الكل",
        aboutWarranty: "عن ضمان",
        beforeSale: "قبل البيع",
        subject: "موضوع التواصل",
        details: "الضمان",
        timeline: "التغطية",
        duration: "المدة",
        daysUnit: "يوم",
        elapsed: "المنقضي",
        unit: "الوحدة",
        customer: "العميل",
        callProvider: "اتصل بالضامن",
        noProvider: "لا ضامن مسجّل",
        holder: "بحوزة",
        under: "على",
    },
    properties: {
        title: "المشاريع والوحدات",
        projects: "المشاريع",
        units: "الوحدات",
        addProject: "مشروع جديد",
        addUnit: "وحدة جديدة",
        addUnits: "عدة وحدات",
        editProject: "تعديل المشروع",
        editUnit: "تعديل الوحدة",
        duplicateUnit: "نسخ",
        searchProjects: "ابحث بالاسم أو المدينة أو رقم المخطط",
        searchUnits: "ابحث برقم الوحدة أو رقم الصك",
        emptyProjects: "لا توجد مشاريع بعد. أنشئ أول مشروع لتبدأ بإضافة الوحدات.",
        emptyUnits: "لا توجد وحدات هنا بعد.",
        emptyUnitsHint: "أضفها واحدة واحدة، أو دورًا كاملًا دفعة واحدة.",
        emptyFiltered: "لا شيء يطابق التصفية التي اخترتها. امسحها لعرض الباقي.",
        emptyBuildings: "لا توجد مبانٍ بعد.",
        emptyBuildingsHint: "أضف مبنى إذا كان المشروع مقسمًا إلى بلوكات أو أبراج.",
        emptyWarranties: "لا توجد ضمانات على هذه الوحدة.",
        emptyWarrantiesHint: "سجّل ما هو مشمول، ولكم من الوقت، ومن الجهة الضامنة.",
        emptyFeatures: "لم تُكتب أي مميزات بعد.",
        shareHint: "هذا رابط الصفحة العامة. يعمل مع أي شخص دون تسجيل دخول.",
        openCatalog: "فتح الصفحة",
        unnamed: "بدون اسم",
        emptyDetails: "لا توجد تفاصيل مسجّلة بعد.",
        emptyDetailsHint: "كل ما تعبّئه في لوحة التعديل يظهر هنا.",
        emptyBuildingsShort: "لا توجد مبانٍ",
        countsHint: "اترك الخانة فارغة لما لا تعرفه بعد.",
        flagsHint: "اختر نعم أو لا، أو الشرطة لما لا تعرفه بعد.",
        tabs: {
            units: "الوحدات",
            buildings: "المباني",
            files: "الملفات",
            details: "التفاصيل",
            warranties: "الضمانات",
        },
        fields: {
            name: "الاسم",
            nameAr: "الاسم (عربي)",
            nameEn: "الاسم (إنجليزي)",
            slug: "الرابط المميز",
            slugHint: "اختياري",
            description: "الوصف",
            types: "أنواع العقارات",
            stage: "المرحلة",
            city: "المدينة",
            address: "العنوان",
            location: "الموقع على الخريطة",
            planNumber: "رقم المخطط",
            plotNumber: "رقم القطعة",
            wafiNumber: "رخصة وافي",
            startedAt: "تاريخ البدء",
            deliveryDate: "تاريخ التسليم",
            notes: "ملاحظات",
            isPublic: "في الكتالوج",
            /** The question a form asks. `isPublic` stays the short noun a chip and a fact label need. */
            showInCatalog: "إظهار في الكتالوج",
            project: "المشروع",
            building: "المبنى",
            number: "رقم الوحدة",
            floor: "في الدور",
            type: "النوع",
            usage: "الاستخدام",
            offer: "معروضة لـ",
            status: "الحالة",
            price: "السعر",
            rentPeriod: "مدة الإيجار",
            rentPeriodHint: "كم شهرًا يغطيها السعر الواحد",
            deedNumber: "رقم الصك",
            useProjectGallery: "استخدام معرض المشروع",
            provider: "الجهة المقدِّمة",
            providerPhone: "جوال الجهة",
            months: "عدد الأشهر",
            numbers: "أرقام الوحدات",
            numbersHint: "اكتب الرقم ثم أضفه. يمكنك كتابة مدى مثل ‎301-312‎ أو لصق أرقام مفصولة بفواصل.",
        },
        groups: { basics: "الأساسيات", measures: "المساحات", counts: "الغرف والأعداد", flags: "المميزات", choices: "المواصفات" },
        measures: { landArea: "مساحة الأرض", builtArea: "المساحة المبنية", streetWidth: "عرض الشارع" },
        counts: {
            bedrooms: "غرف النوم",
            masterBedrooms: "غرف ماستر",
            livingRooms: "الصالات",
            receptions: "المجالس",
            bathrooms: "دورات المياه",
            kitchens: "المطابخ",
            maidRooms: "غرف الخادمة",
            driverRooms: "غرف السائق",
            storageRooms: "المستودعات",
            parkings: "المواقف",
            balconies: "الشرفات",
            pools: "المسابح",
            elevators: "المصاعد",
            floors: "الأدوار داخل الوحدة",
        },
        flags: {
            hasBasement: "قبو",
            hasAnnex: "ملحق",
            hasRoof: "سطح",
            hasGarden: "حديقة",
            hasPrivateEntrance: "مدخل خاص",
            hasDriveway: "ممر سيارات",
            hasLaundryRoom: "غرفة غسيل",
            hasInternalStairs: "درج داخلي",
            hasFittedKitchen: "مطبخ راكب",
            isFurnished: "مؤثثة",
            hasFiberOptic: "ألياف بصرية",
            hasWater: "ماء",
            hasElectricity: "كهرباء",
            hasSewage: "صرف صحي",
            isNew: "جديدة",
        },
        choices: { acSystem: "التكييف", facades: "الواجهات" },
        unitTypes: {
            apartment: "شقة",
            villa: "فيلا",
            duplex: "دوبلكس",
            townhouse: "تاون هاوس",
            floor: "دور",
            studio: "استوديو",
            office: "مكتب",
            shop: "محل",
            warehouse: "مستودع",
            garage: "موقف",
            land: "أرض",
            other: "أخرى",
        },
        stages: { planning: "تخطيط", underConstruction: "تحت الإنشاء", ready: "جاهز" },
        usages: { residential: "سكني", commercial: "تجاري" },
        offers: { sale: "البيع", rent: "الإيجار" },
        statuses: { available: "متاحة", reserved: "محجوزة", sold: "مباعة", rented: "مؤجرة" },
        acSystems: { central: "مركزي", split: "سبليت", window: "شباك", concealed: "مخفي", other: "آخر", none: "بدون" },
        facades: { north: "شمالية", south: "جنوبية", east: "شرقية", west: "غربية" },
        warrantyTypes: {
            structure: "الهيكل الإنشائي",
            waterproofing: "العزل",
            plumbing: "السباكة",
            electrical: "الكهرباء",
            hvac: "التكييف",
            elevators: "المصاعد",
            finishes: "التشطيبات",
            appliances: "الأجهزة",
            other: "أخرى",
        },
        counting: { units: "وحدة", available: "متاحة", buildings: "مبنى" },
        perMonth: "كل {months} أشهر",
        months: "شهرًا",
        sqm: "م²",
        riyal: "ر.س",
        currency: "ريال",
        rentUnits: { month: "شهر", quarter: "ربع سنة", half: "نصف سنة", year: "سنة", months: "شهر" },
        yes: "نعم",
        no: "لا",
        unknown: "غير محدد",
        notSet: "—",
        statusLocked: "حالة الوحدة مرتبطة بالمبيعات حاليًا",
        moveStatus: "تغيير حالة الوحدة",
        confirmStatus: "إرجاع الوحدة إلى «{status}»؟",
        putBackHint: "لا يوجد ما يحجزها، فهذا تصحيح وليس إلغاءً.",
        mapPlaceholder: "الخريطة ستضاف لاحقًا، والموقع المحفوظ محفوظ.",
        confirmRemoveProject: "هل تريد حذف «{name}» وكل وحداته؟",
        confirmRemoveUnit: "هل تريد حذف «{name}»؟",
        confirmRemoveBuilding: "حذف المبنى «{name}»؟",
        confirmRemoveWarranty: "حذف هذا الضمان؟",
        addBuilding: "إضافة مبنى",
        renameBuilding: "تعديل اسم المبنى",
        addWarranty: "إضافة ضمان",
        editWarranty: "تعديل الضمان",
        bulkTitle: "إنشاء عدة وحدات",
        bulkHint: "تُنشأ كل الوحدات بما تملؤه هنا، ثم تُعدَّل الفروقات لاحقًا.",
        bulkPreview: "سيتم إنشاء {count} وحدة",
        nameRequired: "الاسم مطلوب",
        numberRequired: "رقم الوحدة مطلوب",
        projectRequired: "اختر المشروع",
        numbersRequired: "أضف رقم وحدة واحدًا على الأقل",
        incomplete: "ما زالت الملفات قيد الرفع",
    },
    languages: { ar: "عربي", en: "إنجليزي" },
    trash: {
        title: "المحذوفات",
        empty: "لا يوجد شيء محذوف.",
        emptyHint: "تبقى السجلات المحذوفة هنا {days} يومًا، ثم تُحذف نهائيًا.",
        emptyNow: "إفراغ المحذوفات",
        emptyWord: "حذف",
        confirmEmpty: "سيُحذف كل ما هنا نهائيًا مع ملفاته، ولا يمكن التراجع.",
        confirmPurge: "سيُحذف «{name}» نهائيًا مع ملفاته، ولا يمكن التراجع.",
        restore: "استعادة",
        purge: "حذف نهائي",
        deletedAt: "حُذف",
        clearsIn: "يُحذف نهائيًا بعد {days} يومًا",
        clearsToday: "يُحذف نهائيًا اليوم",
        retention: "تُحفظ {days} يومًا",
        models: {
            Project: "المشاريع",
            Unit: "الوحدات",
            Customer: "العملاء",
            Lead: "الاهتمامات",
            Interaction: "التواصل",
            Contract: "العقود",
            Reservation: "الحجوزات",
            InstallmentPlan: "خطط السداد",
            Installment: "الأقساط",
            Warranty: "الضمانات",
        },
    },
    history: { title: "السجل", empty: "لم يحدث شيء لهذا السجل بعد.", system: "النظام", open: "السجل" },
    roles: { owner: "المالك", manager: "مدير", member: "عضو" },
    home: {
        title: "الصفحة الرئيسية",
        greetingMorning: "صباح الخير",
        greetingAfternoon: "طاب يومك",
        greetingEvening: "مساء الخير",
        catalogLabel: "المعرض العام",
        profileLabel: "حسابي",
        workTitle: "ما الجديد؟",
        workClear: "لا يوجد ما ينتظرك — كل شيء محدث.",
        work: {
            followUpsOverdue: "متابعات فات موعدها",
            followUpsToday: "متابعات مستحقة اليوم",
            installmentsOverdue: "أقساط متأخرة",
            reservationsExpiring: "حجوزات تنتهي قريبًا",
            contractsUnsigned: "عقود غير موقّعة",
            leadsStale: "اهتمامات لم يتواصل معها أحد",
        },
        projects: "المشاريع",
        units: "الوحدات",
        customers: "العملاء",
        leads: "اهتمامات مفتوحة",
        shelfTitle: "الوحدات",
        pipelineTitle: "الاهتمامات",
        trendTitle: "آخر ستة أشهر",
        trendLeads: "اهتمامات جديدة",
        trendDeals: "عقود موقّعة",
        collectionsTitle: "الأقساط",
        collected: "محصّل",
        upcoming: "قادم",
        overdue: "متأخر",
        storageTitle: "التخزين",
        storageUsed: "المستخدم",
        storageFree: "المتاح",
        storageOf: "{used} من {allowed}",
        teamTitle: "الفريق",
        subscriptionTitle: "الاشتراك",
        subscriptionDaysLeft: "بقي {days} يوم",
        subscriptionLastDay: "ينتهي اليوم",
        subscriptionEndsAt: "ينتهي في {date}",
        subscriptionNone: "لا يوجد اشتراك ساري",
        subscriptionPending: "هناك طلب دفع في انتظار التأكيد.",
        subscriptionQueued: "هناك تجديد في الانتظار يبدأ مع انتهاء هذه الفترة.",
        subscriptionGrace: "انتهت فترتك — وصول كامل حتى {date}.",
        manage: "إدارة",
        nothingYet: "لا يوجد بعد",
        freeQuota: "أنت على الباقة المجانية",
        readOnly: "انتهى اشتراكك — الحساب للقراءة فقط",
    },
    support: {
        title: "المساعدة",
        ask: "طلب مساعدة",
        askTitle: "طلب مساعدة",
        askHint: "اكتب ما تحتاجه وسنعود إليك. اجعل كل طلب عن أمر واحد.",
        hint: "الطلبات التي ترسلها لنا تظهر هنا مع ردّنا عليها.",
        send: "إرسال",
        statuses: { pending: "بانتظارنا", contacted: "جارٍ العمل عليه", solved: "تم الحل" },
        fields: { subject: "الموضوع", message: "ما الذي تحتاجه؟" },
        replyTitle: "ردّنا",
        detailsTitle: "تفاصيل الطلب",
        answered: "تم الرد",
        noReply: "لم نرد بعد. سيظهر الرد هنا، ولا يوجد إشعار ينتظر — افتح الطلب لتقرأه.",
        hide: "إزالة",
        confirmHide: "إزالة هذا الطلب من قائمتك؟ تبقى نسختنا لدينا، وقد يصلك الرد بعد ذلك.",
        capped: "لديك {max} طلبات مفتوحة، سنجيب عليها قبل أن ترسل طلبًا جديدًا.",
        empty: "لم تطلب منا شيئًا بعد.",
        emptyHint: "فاتورة، شاشة تتصرف بشكل غريب، أو شيء لا تجده — أرسله وسنطّلع عليه.",
    },
    screens: {
        properties: "المشاريع والوحدات",
        crm: "إدارة العملاء",
        drive: "تخزين الملفات",
        team: "إدارة الفريق",
        settings: "الإعدادات",
        subscription: "الاشتراك",
        support: "المساعدة",
        profile: "حسابي",
        notFound: "الصفحة غير موجودة",
    },
};

export default ar;
